// ============================================================================
// DANCE STUDIO CORE - High-Performance Caching & Request Management
// ============================================================================
// Optimized caching system for maximum performance and minimal database calls

// TTL constants for caching (defined early to avoid duplication)
export const CacheTTL = {
  SCHEDULES: 10 * 60 * 1000, // 10 minutes
  ENROLLMENTS: 5 * 60 * 1000, // 5 minutes
  DEFAULT: 5 * 60 * 1000 // 5 minutes
} as const

interface CacheEntry<T> {
  data: T
  timestamp: number
  ttl: number
  accessCount: number // Track access frequency for LRU
  lastAccessed: number // Track last access time
}

interface PendingRequest<T> {
  promise: Promise<T>
  timestamp: number
}

interface CacheStats {
  hits: number
  misses: number
  evictions: number
  totalRequests: number
}

class ApiCache {
  private cache = new Map<string, CacheEntry<any>>()
  private pendingRequests = new Map<string, PendingRequest<any>>()
  private stats: CacheStats = { hits: 0, misses: 0, evictions: 0, totalRequests: 0 }
  
  // Use public TTL constants to avoid duplication
  
  // Cache size limits to prevent memory bloat
  private readonly MAX_CACHE_SIZE = 50
  private readonly MAX_PENDING_REQUESTS = 15
  private readonly CLEANUP_INTERVAL = 2 * 60 * 1000 // 2 minutes
  
  constructor() {
    // Start periodic cleanup
    this.startCleanupTimer()
  }

  /**
   * Get data from cache or execute request with optimized performance
   */
  async get<T>(
    key: string, 
    fetcher: () => Promise<T>, 
    ttl: number = CacheTTL.DEFAULT
  ): Promise<T> {
    this.stats.totalRequests++
    
    // Check if we have valid cached data
    const cached = this.cache.get(key)
    if (cached && this.isValid(cached)) {
      // Update access tracking for LRU
      cached.accessCount++
      cached.lastAccessed = Date.now()
      this.stats.hits++
      return cached.data
    }

    this.stats.misses++

    // Check if there's already a pending request for this key
    const pending = this.pendingRequests.get(key)
    if (pending && this.isRecent(pending.timestamp)) {
      return pending.promise
    }

    // Prevent too many pending requests
    if (this.pendingRequests.size >= this.MAX_PENDING_REQUESTS) {
      console.warn('Too many pending requests, clearing oldest ones')
      this.clearOldestPendingRequests()
    }

    // Create new request
    const promise = this.executeRequest(key, fetcher, ttl)
    this.pendingRequests.set(key, {
      promise,
      timestamp: Date.now()
    })

    try {
      const result = await promise
      return result
    } finally {
      // Clean up pending request
      this.pendingRequests.delete(key)
    }
  }

  /**
   * Execute request and cache result with memory management
   */
  private async executeRequest<T>(
    key: string, 
    fetcher: () => Promise<T>, 
    ttl: number
  ): Promise<T> {
    try {
      const data = await fetcher()
      
      // Check cache size before adding
      if (this.cache.size >= this.MAX_CACHE_SIZE) {
        this.evictLeastRecentlyUsed()
      }
      
      // Cache the result with access tracking
      this.cache.set(key, {
        data,
        timestamp: Date.now(),
        ttl,
        accessCount: 1,
        lastAccessed: Date.now()
      })
      
      return data
    } catch (error) {
      // Don't cache errors, but log them
      console.error(`Cache request failed for key ${key}:`, error)
      throw error
    }
  }

  /**
   * Check if cache entry is still valid
   */
  private isValid(entry: CacheEntry<any>): boolean {
    return Date.now() - entry.timestamp < entry.ttl
  }

  /**
   * Check if pending request is recent (within 30 seconds)
   */
  private isRecent(timestamp: number): boolean {
    return Date.now() - timestamp < 30 * 1000
  }

  /**
   * Evict least recently used entries when cache is full
   */
  private evictLeastRecentlyUsed(): void {
    if (this.cache.size === 0) return

    // Find the least recently used entry
    let lruKey = ''
    let lruTime = Date.now()
    let lruAccessCount = Infinity

    for (const [key, entry] of this.cache) {
      // Prioritize by access count first, then by last accessed time
      if (entry.accessCount < lruAccessCount || 
          (entry.accessCount === lruAccessCount && entry.lastAccessed < lruTime)) {
        lruKey = key
        lruTime = entry.lastAccessed
        lruAccessCount = entry.accessCount
      }
    }

    if (lruKey) {
      this.cache.delete(lruKey)
      this.stats.evictions++
    }
  }

  /**
   * Clear oldest pending requests to prevent memory bloat
   */
  private clearOldestPendingRequests(): void {
    const now = Date.now()
    const maxAge = 60 * 1000 // 1 minute

    for (const [key, request] of this.pendingRequests) {
      if (now - request.timestamp > maxAge) {
        this.pendingRequests.delete(key)
      }
    }
  }

  /**
   * Start periodic cleanup timer
   */
  private startCleanupTimer(): void {
    setInterval(() => {
      this.cleanupExpiredEntries()
      this.clearOldestPendingRequests()
    }, this.CLEANUP_INTERVAL)
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupExpiredEntries(): void {
    let cleaned = 0

    for (const [key, entry] of this.cache) {
      if (!this.isValid(entry)) {
        this.cache.delete(key)
        cleaned++
      }
    }

    if (cleaned > 0) {
      console.log(`Cleaned up ${cleaned} expired cache entries`)
    }
  }

  /**
   * Invalidate cache entries by pattern
   */
  invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear()
      return
    }

    let invalidated = 0
    for (const [key] of this.cache) {
      if (key.includes(pattern)) {
        this.cache.delete(key)
        invalidated++
      }
    }
    
    if (invalidated > 0) {
      console.log(`Invalidated ${invalidated} cache entries matching pattern: ${pattern}`)
    }
  }

  // Removed clearAll method - unused in codebase
}

// Global cache instance
export const apiCache = new ApiCache()

// Cache key generators
export const CacheKeys = {
  SCHEDULES: 'schedules',
  SCHEDULES_BY_CLASS: (classId: number) => `schedules_class_${classId}`,
  ENROLLMENTS: 'enrollments',
  ENROLLMENTS_BY_STATUS: (status: string) => `enrollments_status_${status}`
} as const

// TTL constants moved to top of file to avoid duplication

